import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, Chip, Button, TextField, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  FormControlLabel, Checkbox,
} from "@mui/material";
import httpClient from "../../_util/api";
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";

const STATE_CHIP = {
  warned: { label: "Warned", color: "warning" },
  blocked: { label: "Blocked", color: "error" },
  cleared: { label: "Cleared", color: "default" },
};

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
const hasRole = (role, allowed) => allowed.includes((role || "").toLowerCase());

const GpaWarnings = () => {
  const role = useSelector((state) => state.mainSlice.userdetail?.role);
  const canOverride = hasRole(role, ["approver", "superadmin"]);
  const canClear = hasRole(role, ["reviewer", "superadmin"]);

  const snackbar_Ref = useRef();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [includeCleared, setIncludeCleared] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // { type: 'override' | 'clear', row }
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        search: search.trim(),
      });
      if (includeCleared) params.set("includeCleared", "1");
      const res = await httpClient.get(`/students/gpa/warnings?${params.toString()}`);
      if (res.status === 200) {
        setRows(res.data.warnings || []);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch (error) {
      snackbar_Ref.current?.showMessage(
        "error",
        error?.response?.data?.message || "Failed to load GPA warnings",
        "",
        "i-chk-circle"
      );
    }
  }, [page, rowsPerPage, search, includeCleared]);

  useEffect(() => {
    load();
  }, [load]);

  const openDialog = (type, row) => {
    setReason("");
    setDialog({ type, row });
  };

  const submitDialog = async () => {
    if (!dialog) return;
    const { type, row } = dialog;
    if (type === "override" && !reason.trim()) {
      snackbar_Ref.current?.showMessage("error", "An override reason is required.", "", "i-chk-circle");
      return;
    }
    setBusyId(row.studentId);
    try {
      if (type === "override") {
        await httpClient.post("/students/gpa/override", { studentId: row.studentId, reason: reason.trim() });
        snackbar_Ref.current?.showMessage("success", "Override granted.", "", "i-chk-circle");
      } else {
        await httpClient.post(`/students/gpa/warnings/${row.studentId}/clear`, { reason: reason.trim() });
        snackbar_Ref.current?.showMessage("success", "Warning removed.", "", "i-chk-circle");
      }
      setDialog(null);
      load();
    } catch (error) {
      snackbar_Ref.current?.showMessage(
        "error",
        error?.response?.data?.message || "Action failed",
        "",
        "i-chk-circle"
      );
    } finally {
      setBusyId(null);
    }
  };

  const overridePending = (row) =>
    row.pendingOverride && row.pendingOverride.at && row.pendingOverride.used === false;

  return (
    <>
      <CustomizedSnackbars ref={snackbar_Ref} />
      <Typography variant="h6" fontWeight="bold">
        Low GPA/CGPA Warnings
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
        Students who submitted a scholarship application with GPA/CGPA below 3.00. A first low
        semester issues a warning; a second one blocks the application until a Final Approver grants
        a one-time override. A semester at or above 3.00 clears the warning automatically.
      </Typography>

      <Paper sx={{ width: "100%", overflow: "hidden", padding: 2, marginTop: "16px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <TextField
            size="small"
            label="Search by student or CNIC"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 280 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeCleared}
                onChange={(e) => {
                  setIncludeCleared(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Include cleared"
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#4caf50" }}>
              <TableRow>
                {["#", "Student", "CNIC", "State", "First warning", "Latest low GPA", "Actions"].map((h) => (
                  <TableCell key={h} sx={{ color: "white", fontWeight: "bold" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No GPA warnings.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => {
                  const chip = STATE_CHIP[row.state] || STATE_CHIP.warned;
                  const fw = row.firstWarning || {};
                  const ll = row.lastLowGpa || {};
                  return (
                    <TableRow key={row._id || row.studentId}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.studentName || "—"}</TableCell>
                      <TableCell>{row.CNICNo || "—"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={chip.label} color={chip.color} />
                        {overridePending(row) && (
                          <Typography variant="caption" display="block" sx={{ color: "info.main", mt: 0.5 }}>
                            override pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {fw.at ? (
                          <>
                            GPA <strong>{fw.gpa ?? "—"}</strong>
                            {fw.semester != null ? `, sem ${fw.semester}` : ""}
                            {fw.applicationNumber ? `, App #${fw.applicationNumber}` : ""}
                            <br />
                            <Typography variant="caption" color="textSecondary">
                              {fmt(fw.at)}
                            </Typography>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {ll.at ? (
                          <>
                            GPA <strong>{ll.gpa ?? "—"}</strong>
                            {ll.semester != null ? `, sem ${ll.semester}` : ""}
                            <br />
                            <Typography variant="caption" color="textSecondary">
                              {fmt(ll.at)}
                            </Typography>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {canOverride && row.state === "blocked" && !overridePending(row) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={busyId === row.studentId}
                              onClick={() => openDialog("override", row)}
                            >
                              Grant override
                            </Button>
                          )}
                          {canClear && row.state !== "cleared" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={busyId === row.studentId}
                              onClick={() => openDialog("clear", row)}
                            >
                              Remove warning
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog?.type === "override" ? "Grant one-time override" : "Remove low GPA warning"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialog?.type === "override"
              ? `Allow ${dialog?.row?.studentName || "this student"} to submit one scholarship application despite the repeated low GPA/CGPA. This is recorded for audit.`
              : `Clear the low GPA/CGPA warning for ${dialog?.row?.studentName || "this student"}. Their next low semester will start again at a first warning.`}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={dialog?.type === "override" ? "Reason (required)" : "Reason (optional)"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={dialog?.type === "override" ? "warning" : "error"}
            onClick={submitDialog}
            disabled={busyId != null}
          >
            {dialog?.type === "override" ? "Grant override" : "Remove warning"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GpaWarnings;
